import { CSSProperties } from "@mui/styles";
import { getName, OntologyType } from "core/model/utils/ontologies";
import { useGetOntologies } from "core/model/utils/ontologies/hooks";
import { RadioOption } from "core/types";
import RadioGroup from "ds_legacy/components/RadioGroup";
import RadioGroupV2, {
  RadioGroupV2Props,
} from "ds_legacy/components/RadioGroupV2";
import { indexOf, sortBy } from "lodash";
import { useHideComponent } from "react-forms-state";
import { useTranslations } from "translations";

type OntologyRadioGroupProps = {
  elementName: string;
  excludeOptions?: (number | string)[];
  includeOptions?: (number | string)[];
  isBoldLabel?: boolean;
  isRequired?: boolean;
  label?: string;
  labelStyle?: CSSProperties;
  margin?: string;
  nullOption?: string;
  sort?: (obj: RadioOption) => ReturnType<typeof indexOf>;
  type: OntologyType;
};

function useOptions({
  excludeOptions,
  includeOptions,
  nullOption,
  sort,
  type,
}: {
  excludeOptions?: (number | string)[];
  includeOptions?: (number | string)[];
  nullOption?: string;
  sort?: OntologyRadioGroupProps["sort"];
  type: OntologyType;
}) {
  const getOntologies = useGetOntologies();
  let options = getOntologies({ type }).map((ontology) => ({
    id: ontology.value as number | string,
    label: ontology.label,
    value: ontology.value,
  }));

  if (excludeOptions?.length) {
    options = options.filter(
      (option: RadioOption) => !excludeOptions.includes(option.id),
    );
  }

  if (includeOptions?.length) {
    options = options.filter((option: RadioOption) =>
      includeOptions.includes(option.id),
    );
  }

  if (nullOption) {
    options.push({
      id: "default" as ToType,
      label: nullOption,
      value: -1,
    });
  }
  if (sort) {
    options = sortBy<RadioOption>(options, sort);
  }
  return options;
}

export function OntologyRadioGroupV2({
  elementName,
  excludeOptions,
  includeOptions,
  label,
  nullOption,
  sort,
  type,
  ...props
}: Omit<RadioGroupV2Props, "options" | "label"> &
  Pick<
    OntologyRadioGroupProps,
    | "sort"
    | "type"
    | "includeOptions"
    | "excludeOptions"
    | "nullOption"
    | "label"
  >) {
  const translations = useTranslations();
  const hideComponent = useHideComponent(elementName);
  const options = useOptions({
    excludeOptions,
    includeOptions,
    nullOption,
    sort,
    type,
  });

  if (hideComponent) return null;

  return (
    <RadioGroupV2
      elementName={elementName}
      label={label ? label : getName(type, translations)}
      options={options}
      {...props}
    />
  );
}

export default function OntologyRadioGroup({
  elementName,
  excludeOptions,
  includeOptions,
  isBoldLabel,
  isRequired,
  label,
  labelStyle,
  margin,
  nullOption,
  sort,
  type,
}: OntologyRadioGroupProps) {
  const translations = useTranslations();
  const hideComponent = useHideComponent(elementName);
  const options = useOptions({
    excludeOptions,
    includeOptions,
    nullOption,
    sort,
    type,
  });

  if (hideComponent) return null;

  return (
    <RadioGroup
      elementName={elementName}
      isBold={isBoldLabel}
      isRequired={isRequired}
      label={label != null ? label : getName(type, translations)}
      labelStyle={labelStyle}
      marginWrapper={margin}
      options={options}
    />
  );
}
